"use client";
import { Button } from "@/components/ui/button";
import Input from "@/components/ui/input";
import Indeterminate from "./indeterminate";
import { useMemo, useState } from "react";
import { Column, ColumnDef } from "@tanstack/react-table";
import { Checkbox } from "@/components/ui/checkbox";
import { useDebounce } from "ahooks";
import { useScopedI18n } from "@/messages/client";

export default function CheckboxGroup<T>({
  column,
}: {
  column: Column<T, unknown>;
}) {
  const t = useScopedI18n("选型器");

  const [search, setSearch] = useState("");

  const debouncedValue = useDebounce(search, { wait: 300 });

  const columnFilterValue = (column.getFilterValue() as any[]) ?? [];

  const options = useMemo<{ label: string; value: string }[]>(() => {
    const originOptions = Array.from(
      column.getFacetedUniqueValues().keys()
    ).sort();

    const duplicatesOptions = originOptions.reduce((acc, cur) => {
      if (!cur) {
        acc.push("-");
      }

      if (typeof cur === "string" || typeof cur === "number") {
        acc.push(cur);
        return acc;
      }

      const value = (cur?.data?.display ?? "")
        ?.split(",")
        ?.filter((i: string) => Boolean(i));

      if (Array.isArray(value)) {
        value.forEach((item: string) => {
          acc.push(item);
        });

        return acc;
      }

      return acc;
    }, []);

    const data = [...new Set(duplicatesOptions)].map((item) => ({
      label: item as string,
      value: item as string,
    }));

    return data;
  }, []);

  const sortedUniqueValues = useMemo(() => {
    if (debouncedValue) {
      return (
        options?.filter((v) =>
          v.label.toLowerCase().includes(debouncedValue.toLowerCase())
        ) ?? []
      );
    }

    return options;


  }, [debouncedValue, options]);

  console.log(sortedUniqueValues, "sortedUniqueValues");
  

  return (
    <div className="flex flex-col">
      <div className="flex w-full flex-col">
        <div className="mb-3 mt-4 px-4 md:flex md:items-center md:px-6">
          <div className="relative flex flex-1 items-center">
            <Input
              inputSize="sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-full w-full rounded-none bg-white px-1 text-xs outline-none transition-colors duration-300 focus:border-primary"
              placeholder={t("搜索")}
            />
          </div>
        </div>
        <div className="max-h-36 overflow-hidden">
          <div className="h-full w-full">
            <div className="flex h-10 cursor-pointer items-center border-b border-[#cccccc] px-6">
              <Indeterminate
                id={column.id + "indeterminate"}
                disabled={sortedUniqueValues.length === 0}
                checked={
                  columnFilterValue.length === 0
                    ? false
                    : columnFilterValue.length === sortedUniqueValues.length
                      ? true
                      : "indeterminate"
                }
                onCheckedChange={(check) => {
                  column.setFilterValue(
                    check ? sortedUniqueValues.map((v) => v.value) : []
                  );
                }}
              />
              <label
                htmlFor={column.id + "indeterminate"}
                className="mx-2 cursor-pointer text-xs">
                {t("全选")}
              </label>
            </div>
            <div className="max-h-24 overflow-y-auto">
              {sortedUniqueValues?.map((item) => (
                <div
                  key={item.value}
                  className="flex h-8 cursor-pointer items-center px-6">
                  <Checkbox
                    id={column.id + item.value}
                    checked={columnFilterValue.includes(item.value)}
                    onCheckedChange={(checked) => {
                      column.setFilterValue((old: string[]) => {
                        if (!old) {
                          return [item.value];
                        }

                        if (checked) {
                          return [...old, item.value];
                        } else {
                          return old.filter((v) => v !== item.value);
                        }
                      });
                    }}
                  />
                  <label
                    htmlFor={column.id + item.value}
                    className="mx-2 cursor-pointer text-xs">
                    {item.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="px-4 md:py-4 py-2">
        <Button
          className="h-auto p-0 text-sm font-normal text-primary hover:bg-transparent"
          variant="ghost"
          onClick={() => {
            column.setFilterValue(undefined);
            setSearch("");
          }}>
          {t("复位")}
        </Button>
      </div>
    </div>
  );
}
